// ============================================================
// AD Scorecard — access control + turnover math
// ============================================================

// ── Who can see the AD Scorecard section ────────────────────────────────────
// These must match the UUIDs in the RLS policy in ad_scorecard.sql — the UI
// check hides the nav item, the RLS policy is what actually protects the data.
export const AD_SCORECARD_VIEWERS = [
  '5dff583e-c52d-4954-b09f-a3334f7590d1',  // Lauren Parker
  'a4a45550-b175-422b-ab0b-9d5d4c0e69b1',  // Tori Gilmore
]

// ── Profiles to keep off the scorecard entirely ─────────────────────────────
// Test/duplicate accounts. Add ids here to hide them.
export const AD_SCORECARD_EXCLUDED = [
  '4c5cfdde-c211-42e8-8481-7c021400a0f6',  // Lauren Parker TEST
]

// Who counts as an Account Director for scorecard purposes.
// Role alone isn't enough — someone can carry the `am` role and still be the
// AD of record on accounts (Jade), so anyone assigned as account_director_id
// on a client is included too.
export const resolveDirectors = (profiles = [], clients = []) => {
  const ids = new Set()
  profiles.forEach(p => {
    if (p.role === 'ad' || p.role === 'supervising_ad') ids.add(p.id)
  })
  clients.forEach(c => {
    if (c.account_director_id) ids.add(c.account_director_id)
  })
  AD_SCORECARD_EXCLUDED.forEach(id => ids.delete(id))

  return profiles
    .filter(p => ids.has(p.id))
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
}

// ── Turnover targets ────────────────────────────────────────────────────────
// Goal is 3-5% monthly. GOAL is the number to hit; STRETCH is the better one.
export const TARGET_MONTHLY = 5
export const STRETCH_MONTHLY = 3

// Turns a target rate into a concrete client count: at `base` clients, losing
// more than this many in a month breaks the target. Rounded down — losing 2.7
// clients isn't a thing, and 3 would already miss.
export const targetProgress = (base, actualTerminations, targetPct) => {
  if (!base) return null
  const allowed = Math.floor((targetPct / 100) * base)
  const actual = actualTerminations ?? 0
  return {
    allowed,
    actual,
    gap: actual - allowed,        // >0 means over target by this many clients
    met: actual <= allowed,
    ratePct: (actual / base) * 100,
  }
}

// Annual equivalents of the monthly targets, for the rolling-12mo and
// annualized cards. 5%/mo -> 60% rolling / 46% annualized; 3%/mo -> 36% / 31%.
export const TARGET_ROLLING = TARGET_MONTHLY * 12          // 60%
export const STRETCH_ROLLING = STRETCH_MONTHLY * 12        // 36%
export const TARGET_ANNUAL = (1 - Math.pow(1 - TARGET_MONTHLY / 100, 12)) * 100
export const STRETCH_ANNUAL = (1 - Math.pow(1 - STRETCH_MONTHLY / 100, 12)) * 100

// How far an annual figure is from target, expressed in clients per year —
// the only unit anyone can act on. `avgBase` is the average client count over
// the window; `actualPct` is the rolling or annualized rate.
export const annualTargetProgress = (avgBase, actualPct, targetPct) => {
  if (!avgBase || actualPct === null || actualPct === undefined) return null
  const allowedPerYear = Math.floor((targetPct / 100) * avgBase)
  const actualPerYear = Math.round((actualPct / 100) * avgBase)
  return {
    allowedPerYear,
    actualPerYear,
    gap: actualPerYear - allowedPerYear,
    met: actualPct <= targetPct,
    targetPct,
  }
}

// ── Churn by cause ──────────────────────────────────────────────────────────
// The split that matters in a 1:1. An AD can move the controllable bucket;
// they cannot move whether sales signed someone who couldn't pay.
export const CONTROLLABLE_REASONS = ['value_not_shown', 'results', 'expectations', 'relationship']
export const NOT_CHURN_REASONS = ['scope_complete', 'paused', 'business_closed']

export const REASON_LABELS = {
  value_not_shown: "Couldn't justify cost",
  results: 'Results',
  expectations: 'Expectations',
  relationship: 'Relationship',
  cant_afford: 'Genuinely unaffordable',
  scope_complete: 'Setup / one project only',
  paused: 'Paused, may return',
  business_closed: 'Business closed',
  other: 'Unrecorded',
}

// Sums the jsonb reason maps across a set of rows into one tally.
export const sumReasons = (rows = []) => {
  const out = {}
  rows.forEach(r => {
    const m = r.terminating_by_reason
    if (!m) return
    Object.entries(m).forEach(([k, v]) => { out[k] = (out[k] || 0) + (Number(v) || 0) })
  })
  return out
}

// Controllable churn is the number to coach on. Everything else is a pricing,
// qualification, or bookkeeping question.
export const splitChurn = (tally = {}) => {
  let controllable = 0, structural = 0, notChurn = 0, unknown = 0
  Object.entries(tally).forEach(([k, v]) => {
    const n = Number(v) || 0
    if (CONTROLLABLE_REASONS.includes(k)) controllable += n
    else if (k === 'cant_afford') structural += n
    else if (NOT_CHURN_REASONS.includes(k)) notChurn += n
    else unknown += n
  })
  return { controllable, structural, notChurn, unknown, total: controllable + structural + notChurn + unknown }
}

// Sentinel id for the company-wide roll-up entry in the AD list.
export const COMPANY_ID = '__company__'

// Company totals are a SUM across ADs, not an average of their rates. Turnover
// doesn't average — an AD with 8 clients would distort it just as much as one
// with 30. Sum the terminations, sum the base, then divide once.
// `history` rows come from ad_company_history — real company totals for months
// before per-AD tracking began. They win for their month; the AD roll-up only
// fills months history doesn't cover.
export const buildCompanyRows = (snapshots = [], history = []) => {
  const byMonth = {}
  snapshots.forEach(r => {
    const m = byMonth[r.month] || (byMonth[r.month] = {
      id: `company-${r.month}`, profile_id: COMPANY_ID, month: r.month,
      total_clients: 0, am_clients: 0, new_clients: 0,
      terminating_auto: 0, terminating_override: null, locked: true,
      terminating_by_reason: null, tenure_buckets: null,
    })
    m.total_clients += r.total_clients || 0
    m.am_clients += r.am_clients || 0
    m.new_clients += r.new_clients || 0
    // Roll up each AD's effective figure so hand-typed corrections carry through.
    m.terminating_auto += (r.terminating_override ?? r.terminating_auto ?? 0)
    // Merge each AD's reason tally into the company one. Without this the
    // company view has counts but no causes.
    if (r.terminating_by_reason) {
      m.terminating_by_reason = m.terminating_by_reason || {}
      Object.entries(r.terminating_by_reason).forEach(([k, v]) => {
        m.terminating_by_reason[k] = (m.terminating_by_reason[k] || 0) + (Number(v) || 0)
      })
    }
    // Same treatment for tenure — without this the company view has counts but
    // no "how fast they leave" breakdown, which is exactly what happened.
    if (r.tenure_buckets) {
      m.tenure_buckets = m.tenure_buckets || {}
      Object.entries(r.tenure_buckets).forEach(([k, v]) => {
        m.tenure_buckets[k] = (m.tenure_buckets[k] || 0) + (Number(v) || 0)
      })
    }
  })
  history.forEach(h => {
    byMonth[h.month] = {
      id: `history-${h.month}`, profile_id: COMPANY_ID, month: h.month,
      total_clients: h.total_clients || 0,
      am_clients: 0,
      new_clients: h.new_clients || 0,
      terminating_auto: h.terminating_clients || 0,
      terminating_override: null,
      locked: true,
      from_history: true,
      // ad_company_history carries its own reason breakdown — the seeded
      // 2026 data lives here, not on the per-AD snapshots.
      terminating_by_reason: h.terminating_by_reason || null,
      tenure_buckets: h.tenure_buckets || null,
    }
  })

  return Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month))
}

// "If this month's rate held for a year." Simple form (rate x 12) overstates,
// because churn compounds on a shrinking base — so also return the share of
// clients actually still around after 12 months, and the implied tenure.
export const annualize = (monthlyPct) => {
  if (monthlyPct === null || monthlyPct === undefined || Number.isNaN(monthlyPct)) return null
  const r = monthlyPct / 100
  const retained = Math.pow(1 - r, 12)
  return {
    simple: monthlyPct * 12,                       // headline figure
    attrition: (1 - retained) * 100,               // share lost over 12 months
    retention: retained * 100,                     // share still here
    tenureMonths: r > 0 ? 1 / r : null,            // average client lifespan
  }
}

// Full access: every AD's card plus the company roll-up.
export const canViewAllScorecards = (userId) =>
  !!userId && AD_SCORECARD_VIEWERS.includes(userId)

// An AD can open the section to see their own card and the company view — but
// not another AD's. Comparing individuals is a conversation Tori should own,
// not something people stumble into.
export const canViewADScorecard = (userId, profile) =>
  canViewAllScorecards(userId) ||
  ['ad', 'supervising_ad', 'am'].includes(profile?.role)

// Which cards a given person may open.
export const visibleDirectorsFor = (userId, profile, directors) =>
  canViewAllScorecards(userId) ? directors : directors.filter(d => d.id === userId)

// ── Month helpers ───────────────────────────────────────────────────────────
// Months are stored as the 1st of the month, 'YYYY-MM-01'.
export const monthKey = (date) => {
  const d = date instanceof Date ? date : new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

export const currentMonthKey = () => monthKey(new Date())

export const monthLabel = (key) => {
  if (!key) return ''
  const [y, m] = String(key).split('-')
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${names[Number(m) - 1]} ${y}`
}

// ── Terminations: computed number unless someone typed an override ──────────
export const effectiveTerminating = (row) =>
  row?.terminating_override ?? row?.terminating_auto ?? 0

// ============================================================================
// TURNOVER RATE
//
// total_clients is an END-of-month count, so the client base a month started
// with is simply the previous month's total. Standard client churn:
//
//     turnover % = terminations this month / clients at start of month * 100
//
// The first month on record has no prior month, so it returns null and the UI
// shows a dash rather than a fake 0%.
// ============================================================================
export const turnoverRate = (row, prevRow) => {
  if (!row || !prevRow) return null
  const base = prevRow.total_clients
  if (!base) return null
  return (effectiveTerminating(row) / base) * 100
}

// Net growth for the month: gained minus lost.
export const netGrowth = (row) =>
  (row?.new_clients ?? 0) - effectiveTerminating(row)

// ── Rolling 12-month turnover ───────────────────────────────────────────────
// A single month is noisy at this client volume — 5 terminations out of 24 is
// 20.8% for one month but may be nothing structural. This annualized figure is
// the more honest number to take into an AD conversation.
//
//   rolling % = terminations over the window / average client count * 100
//
// `rows` must be sorted oldest -> newest. Returns null until there are at least
// 3 months of history, since anything shorter isn't a meaningful trend.
export const rollingTurnover = (rows, upToIndex, windowSize = 12) => {
  if (!Array.isArray(rows) || upToIndex < 0) return null
  const start = Math.max(0, upToIndex - windowSize + 1)
  const window = rows.slice(start, upToIndex + 1)
  if (window.length < 3) return null

  const terminations = window.reduce((sum, r) => sum + effectiveTerminating(r), 0)
  const avgClients = window.reduce((sum, r) => sum + (r.total_clients || 0), 0) / window.length
  if (!avgClients) return null

  return (terminations / avgClients) * 100
}

export const formatPct = (value, digits = 1) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : `${value.toFixed(digits)}%`

// Color bands for a MONTHLY turnover figure. Target is 3-5%/mo.
export const turnoverTone = (pct) => {
  if (pct === null || pct === undefined) return { color: 'var(--text-muted)', bg: 'transparent' }
  if (pct >= 15) return { color: 'var(--red)', bg: 'var(--red-bg)' }
  if (pct >= 8) return { color: '#c4962a', bg: '#fff8e1' }
  return { color: 'var(--green)', bg: 'var(--lightgreen-bg)' }
}

// Color bands for ANNUAL figures (rolling 12mo, annualized attrition). These
// are ~12x a monthly rate, so monthly thresholds would paint everything red.
// 3%/mo target -> ~36% rolling; 5%/mo -> ~60%.
export const annualTone = (pct) => {
  if (pct === null || pct === undefined) return { color: 'var(--text-muted)', bg: 'transparent' }
  if (pct >= 75) return { color: 'var(--red)', bg: 'var(--red-bg)' }
  if (pct >= 45) return { color: '#c4962a', bg: '#fff8e1' }
  return { color: 'var(--green)', bg: 'var(--lightgreen-bg)' }
}

// ── Watchlist ───────────────────────────────────────────────────────────────
// Clients Tori and an AD agree need extra attention, identified in the monthly
// check-in. Reusing the AD scorecard's config keeps the two views consistent.
export const WATCH_REASONS = [
  { key: 'bad_data',     label: 'Numbers are poor',
    hint: 'Results genuinely are not there.',
    focus: 'Fix the work, then show the fix. Do not lead with explanation.' },
  { key: 'complaining',  label: 'They have complained',
    hint: 'Something specific has been raised.',
    focus: 'Close the specific thing first. General reassurance reads as evasion.' },
  { key: 'concerned',    label: 'We are concerned',
    hint: 'No complaint yet, but the read is bad.',
    focus: 'Get in front of it while it is still your idea to talk.' },
  { key: 'stagnant',     label: 'Stagnant — needs something new',
    hint: 'Data is fine, the account has gone flat.',
    focus: 'Bring an idea they did not ask for. Competence alone stops impressing.' },
  { key: 'at_risk',      label: 'At risk of leaving',
    hint: 'We think this one may go.',
    focus: 'Tori on the next call. Scope options ready before they ask.' },
  { key: 'relationship', label: 'Relationship needs repair',
    hint: 'The working relationship itself.',
    focus: 'Person-to-person, not reporting. Often a different person should call.' },
  { key: 'delivery',     label: 'We are behind',
    hint: 'Late work they know about.',
    focus: 'Deliver before explaining. Then a new date you will actually hit.' },
  { key: 'other',        label: 'Other', hint: '', focus: '' },
]

export const getWatchReason = k => WATCH_REASONS.find(r => r.key === k)

export const WATCH_SEVERITY = {
  watch:   { label: 'Watch',   color: 'var(--text-muted)', bg: 'var(--cream)',
             note: 'Keep an eye on it' },
  concern: { label: 'Concern', color: '#c4962a',           bg: '#fff8e1',
             note: 'Needs a plan this month' },
  urgent:  { label: 'Urgent',  color: 'var(--red)',        bg: 'var(--red-bg)',
             note: 'Acting this week' },
}

export const WATCH_STATUS = {
  open:      { label: 'Open',      color: 'var(--navy)' },
  improving: { label: 'Improving', color: 'var(--teal)' },
  resolved:  { label: 'Back on track', color: 'var(--green)' },
  lost:      { label: 'Lost',      color: 'var(--red)' },
}

// ── Speed of churn ──────────────────────────────────────────────────────────
// Why clients leave and how fast they leave are different questions with
// different fixes. Losing them at month 2 is onboarding and expectations;
// losing them at month 14 is results and relationship.
export const TENURE_BUCKETS = [
  { key: '0-3 months',  label: '0–3 mo',  color: 'var(--red)',
    means: 'Onboarding and expectations — they left before the work could work.' },
  { key: '3-6 months',  label: '3–6 mo',  color: '#c4962a',
    means: 'Value not shown — results were still coming and they stopped believing.' },
  { key: '6-12 months', label: '6–12 mo', color: 'var(--navy-mid)',
    means: 'Results or relationship — long enough to judge both.' },
  { key: '12+ months',  label: '12+ mo',  color: 'var(--teal)',
    means: 'A real run. Often circumstance rather than failure.' },
]

// Months a client survived before terminating.
export const tenureMonths = (startDate, archivedAt) => {
  if (!startDate || !archivedAt) return null
  const s = new Date(String(startDate).slice(0, 10) + 'T12:00:00')
  const e = new Date(String(archivedAt).slice(0, 10) + 'T12:00:00')
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null
  return (e - s) / 86400000 / 30.44
}

export const tenureBucket = (months) => {
  if (months == null) return null
  if (months <= 3) return '0-3 months'
  if (months <= 6) return '3-6 months'
  if (months <= 12) return '6-12 months'
  return '12+ months'
}

// Reads the tenure_buckets stored on the snapshot rows — the same source the
// reasons chart uses. Computing from the live clients table instead would only
// see terminations that were entered in the app, which is a fraction of them,
// and the two cards would disagree.
export const churnSpeedFromRows = (rows = []) => {
  const buckets = {}
  rows.forEach(r => {
    if (!r.tenure_buckets) return
    Object.entries(r.tenure_buckets).forEach(([k, v]) => {
      buckets[k] = (buckets[k] || 0) + (Number(v) || 0)
    })
  })
  const total = Object.values(buckets).reduce((a, b) => a + b, 0)
  if (!total) return null

  // Median from grouped data: find the bucket the middle value falls in and
  // interpolate across it. Exact to within the bucket width, which is all the
  // precision the underlying data supports anyway.
  const EDGES = { '0-3 months': [0, 3], '3-6 months': [3, 6], '6-12 months': [6, 12], '12+ months': [12, 24] }
  const order = ['0-3 months', '3-6 months', '6-12 months', '12+ months']
  let seen = 0, median = null
  const half = total / 2
  for (const k of order) {
    const n = buckets[k] || 0
    if (n === 0) continue
    if (seen + n >= half) {
      const [lo, hi] = EDGES[k]
      median = lo + ((half - seen) / n) * (hi - lo)
      break
    }
    seen += n
  }

  const within90 = buckets['0-3 months'] || 0
  const within180 = within90 + (buckets['3-6 months'] || 0)
  return {
    total, buckets, median: median ?? 0, within90, within180,
    pct90: (within90 / total) * 100,
  }
}

// Kept for anywhere that genuinely wants live client records rather than
// snapshot history.
export const churnSpeed = (clients = [], profileId = null) => {
  const rows = clients.filter(c =>
    c.archived_at && (!profileId || c.account_director_id === profileId))
  // Termination date when we have it; archive date for older records.
  const months = rows
    .map(c => tenureMonths(c.start_date || c.created_at, c.terminated_on || c.archived_at))
    .filter(m => m != null && m >= 0)
    .sort((a, b) => a - b)
  if (!months.length) return null

  const mid = Math.floor(months.length / 2)
  const median = months.length % 2 ? months[mid] : (months[mid - 1] + months[mid]) / 2

  const buckets = {}
  months.forEach(m => {
    const b = tenureBucket(m)
    buckets[b] = (buckets[b] || 0) + 1
  })

  return {
    total: months.length,
    median,
    buckets,
    within90: months.filter(m => m <= 3).length,
    within180: months.filter(m => m <= 6).length,
    pct90: (months.filter(m => m <= 3).length / months.length) * 100,
  }
}